import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from '@reduxjs/toolkit';
import merge from 'deepmerge';
import { PURGE } from 'redux-persist';

import { rtkClient } from '~/api';
import type { AlertDto } from '~/api/generated-api';
import type { RootState } from '~/app/rootReducer';
import {
  ALERTS_API_CONFIG,
  API_VERSION_DEFAULTS,
} from '~/common/apis/api.constants';
import type { RouteParams } from '~/common/configs/route.config';
import {
  type AlertCardAssetAlerts,
  type AlertDetail,
  type AlertEvent,
  AlertQueryFieldType,
  type AlertSubscription,
  type ApiAlertEntity,
  type AssetAlert,
  type AvailableSubscription,
  type Subscription,
  type SubscriptionType,
} from '~/common/models/alert.model';
import type * as Types from '~/common/models/alerts.model';
import {
  type ApiResponse,
  BDRequestStatus,
  type BDRequestType,
  type OperationStatus,
} from '~/common/models/apis/apiResponse.model';
import { ApiAssetType } from '~/common/models/asset.model';
import {
  DateRange as AlertDateRange,
  type ListViewSession,
  type PagedResult,
  type PagedResultWithErrors,
  type SearchCriteria,
} from '~/common/models/common.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import type { FeatureFlagValueType } from '~/common/models/featureFlags.model';
import type { BDQuery } from '~/common/models/query.model';
import {
  addAcceptLanguageHeader,
  hasApiResult,
} from '~/common/utils/apis/api.utils';
import {
  makePostPayloadCreator,
  makeThunk,
} from '~/common/utils/store/thunk.helper';
import { selectFeatureFlagById } from '~/store/featureFlags/featureFlagsSlice';
import { mapAlertApiEntityToAlertHistoryDetail } from '~/utils/alerts/alert.mappers';
import { createAlertsEntitiesQuery } from '~/utils/alerts/alerts.utils';

export const DEFAULT_ALERT_SORT: AlertSort = {
  properties: [AlertQueryFieldType.TIMESTAMP],
  direction: 'DESC',
};

export enum AlertSessionViewType {
  FEED = 'Feed List',
  SUBSCRIPTION = 'Subscription List',
  DASHBOARD = 'Dashboard List',
  MAP_DETAILS = 'Map Details',
  ASSET_DETAILS = 'Asset Details',
  HISTORY = 'History List',
}

export enum AlertRequestType {
  GET_ALERT_UNREAD = 'Get Alert Unread',
  PUT_ALERT_UNREAD = 'Put Alert Unread',
  POST_ALERT_UNREAD = 'Post Alert Unread',
  POST_ALERT_READ = 'Post Alert Read',
  GET_SUBSCRIPTIONS = 'Get User Subscriptions',
  POST_SUBSCRIPTIONS = 'Post User Subscription',
  PUT_SUBSCRIPTIONS = 'PUT User Subscription',
  GET_SUBSCRIPTION_TYPES = 'Get User Subscription Types',
  GET_AVAILABLE_SUBSCRIPTIONS = 'Merged GET_SUBSCRIPTIONS and GET_SUBSCRIPTION_TYPES',
}

export interface AlertSort {
  direction: 'ASC' | 'DESC';
  properties: Array<string>;
}

export interface AlertsPayload {
  page: number;
  size: number;
  sort?: Array<AlertSort>;
  token?: string;
  direct?: boolean;
  query?: BDQuery;
}

export interface AlertsPutUnreadRequest {
  lastUnread: string;
}

export interface AlertHistorySession
  extends Record<keyof AlertDetail, string>,
    ListViewSession {}
export interface AlertSubscriptionSession
  extends Record<keyof AlertSubscription, string>,
    ListViewSession {}

export interface AlertListFilterSession {
  selectedAssetTypes?: ApiAssetType[];
  selectedAlertTypes?: AlertEvent[];
  selectedDateRange?: AlertDateRange;
  searchCriteria?: SearchCriteria;
}

export type AlertDetailsState = Partial<ListViewSession> & {
  operationStatus: OperationStatus;
  assetAlerts?: AlertCardAssetAlerts[];
};

export type AlertsState = Partial<ListViewSession> & {
  operationStatus: OperationStatus;
  alerts?: AssetAlert[];
};

export type AlertsEntitiesState = Partial<ListViewSession> & {
  operationStatus: OperationStatus;
  entities?: (AlertCardAssetAlerts & { alertsState?: AlertsState })[];
};

export type AlertParams = Partial<ListViewSession> & {
  viewType: AlertSessionViewType;
  sessionId: string;
} & Pick<RouteParams, 'organizationsId' | 'hubsId' | 'fleetsId' | 'assetsId'>;

export type DetailsState = {
  [key: string]: {
    [key: string]: AlertDetailsState;
  };
};

export type AlertsEntitiesSessionState = {
  [key: string]: {
    [key: string]: AlertsEntitiesState;
  };
};

export type AlertsSessionState = {
  [key: string]: {
    [key: string]: {
      [key: string]: AlertsState;
    };
  };
};

export type AlertsFiltersSessionState = {
  [key in AlertSessionViewType]?: {
    [key: string]: AlertListFilterSession;
  };
};

export interface AlertSessionConfigType {
  [AlertSessionViewType.FEED]?: {
    [id: string]: Partial<ListViewSession>;
  };
  [AlertSessionViewType.SUBSCRIPTION]?: {
    [id: string]: Partial<AlertHistorySession>;
  };
  [AlertSessionViewType.DASHBOARD]?: {
    [id: string]: Partial<AlertDetailsState>;
  };
  [AlertSessionViewType.MAP_DETAILS]?: {
    [id: string]: Partial<AlertDetailsState>;
  };
  [AlertSessionViewType.ASSET_DETAILS]?: {
    [id: string]: Partial<AlertDetailsState>;
  };
  [AlertSessionViewType.HISTORY]?: {
    [id: string]: Partial<AlertDetailsState>;
  };
}

export const DEFAULT_MAX_ALERTS_REQUEST_SIZE = 200;
export const DEFAULT_ALERTS_ROW_PER_PAGE = 20;
export const DEFAULT_ALERTS_DATE_RANGE = AlertDateRange.TODAY;

interface AlertState {
  entitiesSessions: AlertsEntitiesSessionState;
  filterSessions: AlertsFiltersSessionState;
  subscriptions: EntityState<Subscription>;
  subscriptionTypes: EntityState<SubscriptionType>;
  operations: {
    [key in BDRequestType | AlertRequestType]?: OperationStatus;
  };
  rowsPerPage?: number;
  editableSubscriptionId?: string;
  highlightedSubscriptionId?: string;
  alerts: EntityState<AlertDto>;
  totalAlerts: number;
  popUpIsOpen: boolean;
}

const CONFIG = ALERTS_API_CONFIG;

export const subscriptionsAdapter = createEntityAdapter<Subscription>({
  selectId: (sub: Subscription) => `${sub.category}-${sub.event}`,
});
export const subscriptionTypeAdapter = createEntityAdapter<SubscriptionType>({
  selectId: (type: SubscriptionType) => `${type.category}-${type.event}`,
});

export const availableSubscriptionsAdapter =
  createEntityAdapter<AvailableSubscription>({
    selectId: (availableSubscription: AvailableSubscription) =>
      `${availableSubscription.category}-${availableSubscription.event}`,
  });

export const alertsAdapter = createEntityAdapter<AlertDto>({
  selectId: (notification: AlertDto) => notification.id ?? '',
});

const initialAlertState: AlertState = {
  entitiesSessions: {},
  filterSessions: {},
  operations: {},
  subscriptions: subscriptionsAdapter.getInitialState(),
  subscriptionTypes: subscriptionTypeAdapter.getInitialState(),
  editableSubscriptionId: undefined,
  highlightedSubscriptionId: undefined,
  alerts: alertsAdapter.getInitialState(),
  totalAlerts: 0,
  popUpIsOpen: false,
};

export const getAlertsEntities = makeThunk(
  'alerts/getAlertsEntities',
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AlertCardAssetAlerts>>,
    AlertParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/alerts/${API_VERSION_DEFAULTS.alertsAPI}/me/entities`,
    axiosOptions: (_, state: RootState) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (
      { page, rowsPerPage: size, viewType, sessionId, ...params },
      state
    ) => {
      const sessionConfig = {
        ...state.alerts?.filterSessions?.[viewType]?.[sessionId],
        selectedAssetTypes: [ApiAssetType.ELCV],
      };
      const query = createAlertsEntitiesQuery(params, sessionConfig);
      return {
        requestBody: {
          page,
          size,
          query,
        },
      };
    },
    responseAdapter: (
      response: unknown | ApiResponse<unknown>,
      state,
      params
    ) => {
      if (response && hasApiResult<PagedResult<ApiAlertEntity>>(response)) {
        const errors: BDError[] = [];
        const { items, total_items } = response.result;
        const result = {
          ...response,
          result: {
            total_items,
            items: items?.length
              ? response.result.items.reduce((mappedAlerts, apiAlertDetail) => {
                  try {
                    mappedAlerts.push(
                      mapAlertApiEntityToAlertHistoryDetail(
                        apiAlertDetail,
                        params,
                        state.profile.allowedContexts
                      )
                    );
                  } catch (e: unknown) {
                    errors.push({
                      name: 'Get Alerts Entities error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map Alert API response tp alert history',
                      data: apiAlertDetail,
                    });
                  }
                  return mappedAlerts;
                }, [] as AlertCardAssetAlerts[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      throw new BDError('Unexpected alert list response', { data: response });
    },
  })
);

export const alertsSlice = createSlice({
  name: 'alerts',
  initialState: initialAlertState,
  reducers: {
    setAlertsFilterSession: (
      state,
      action: PayloadAction<Partial<AlertsFiltersSessionState>>
    ) => {
      Object.keys(action.payload).forEach((key) => {
        const scope = key as AlertSessionViewType;
        const payload = action.payload[scope] || {};
        Object.keys(payload).forEach((id) => {
          state.filterSessions = merge(
            state.filterSessions,
            {
              [scope]: {
                [id]: payload[id] && {
                  ...payload[id],
                },
              },
            },
            {
              arrayMerge: (_, sourceArray) => sourceArray,
            }
          );
        });
      });
    },
    setEditableSubscriptionId: (state, action) => {
      state.editableSubscriptionId = action.payload;
    },
    setHighlightedSubscriptionId: (state, action) => {
      state.highlightedSubscriptionId = action.payload;
    },
    setPopUp: (state, action: Types.SetPopUpAction) => {
      state.popUpIsOpen = action.payload;
    },
    addAlert(state, action: Types.AlertAction) {
      alertsAdapter.addOne(state.alerts, action.payload);
    },
    updateAlert(state, action: Types.AlertAction) {
      alertsAdapter.updateOne(state.alerts, {
        id: action.payload.id ?? '',
        changes: { ...action.payload },
      });
    },
    setAlerts(state, action: Types.AlertsAction) {
      alertsAdapter.setMany(state.alerts, action.payload.alerts);
      state.totalAlerts = action.payload.totalAlerts;
    },
    replaceAlerts(state, action: Types.AlertsAction) {
      alertsAdapter.setAll(state.alerts, action.payload.alerts);
      state.totalAlerts = action.payload.totalAlerts;
    },
    removeAlerts(state, action: PayloadAction<string[]>) {
      alertsAdapter.removeMany(state.alerts, action.payload);
    },
    setAllRead(state) {
      state.alerts.ids.forEach((id) => {
        alertsAdapter.updateOne(state.alerts, {
          id,
          changes: { read: true },
        });
      });
    },
  },
  extraReducers: (builder) => {
    // alerts entities
    builder.addCase(getAlertsEntities.pending, (state, action) => {
      const { sessionId, viewType } = action.meta.arg;
      const safeSessionId = '' + sessionId;
      state.entitiesSessions = merge(
        state.entitiesSessions,
        {
          [viewType]: {
            [safeSessionId]: {
              count: undefined,
              entities: undefined,
              rowsExpanded: undefined,
              operationStatus: {
                status: BDRequestStatus.PENDING,
                errors: [],
              },
            },
          },
        },
        {
          arrayMerge: (_, sourceArray) => sourceArray,
        }
      );
    });
    builder.addCase(getAlertsEntities.fulfilled, (state, action) => {
      const { sessionId, viewType, ...session } = action.meta.arg;
      const safeSessionId = '' + sessionId;
      state.entitiesSessions = merge(
        state.entitiesSessions,
        {
          [viewType]: {
            [safeSessionId]: {
              ...session,
              count: action.payload.result?.total_items,
              entities: action.payload.result.items,
              operationStatus: {
                status: BDRequestStatus.SUCCEEDED,
              },
            },
          },
        },
        {
          arrayMerge: (_, sourceArray) => sourceArray,
        }
      );
    });
    builder.addCase(getAlertsEntities.rejected, (state, action) => {
      const { sessionId, viewType } = action.meta.arg;
      const safeSessionId = '' + sessionId;
      state.entitiesSessions = merge(
        state.entitiesSessions,
        {
          [viewType]: {
            [safeSessionId]: {
              operationStatus: {
                status: BDRequestStatus.FAILED,
                errors: [
                  {
                    type: BDAppErrorType.API,
                    ...(action.payload || (action.error as BDError)),
                  },
                ],
              },
            },
          },
        },
        {
          arrayMerge: (_, sourceArray) => sourceArray,
        }
      );
    });

    // reset state when persist store is purged on logout
    builder.addCase(PURGE, () => {
      return initialAlertState;
    });
  },
});

export const {
  setAlertsFilterSession,
  setEditableSubscriptionId,
  setHighlightedSubscriptionId,
  setPopUp,
  addAlert,
  updateAlert,
  setAlerts,
  replaceAlerts,
  removeAlerts,
  setAllRead,
} = alertsSlice.actions;

export const alertsReducer = alertsSlice.reducer;

export const {
  useGetAlertsForCurrentUserQuery,
  useUpdateAllAlertsToReadAsyncMutation,
} = rtkClient;
